import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

type ProjectRow = {
  id: number;
  name: string;
  description?: string;
  tenantId?: number;
  createdAt?: string;
  deletedAt?: string | null;
  testCyclesCount?: number;
  activityCount?: number;
};

type AssignedUserRow = {
  userId: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status?: string;
};

const ROLE_OPTIONS = ['ADMIN', 'MEMBER', 'PROJECT_MANAGER', 'ADMIN_MEMBER'];

const SuperAdminProjectManagementPage: React.FC = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [projects, setProjects] = useState<ProjectRow[]>([]);

  const createdByDefault = useMemo(() => {
    const u = user as any;
    const first = (u?.firstName || u?.first_name || '').trim();
    const last = (u?.lastName || u?.last_name || '').trim();
    if (first || last) return `${first} ${last}`.trim();
    return (u?.email || '').trim();
  }, [user]);

  const loadProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminProjectManagementProjects(includeArchived);
      setProjects(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    tenantId: '',
    name: '',
    description: '',
    createdBy: createdByDefault,
  });

  useEffect(() => {
    setCreateForm((prev) => ({ ...prev, createdBy: createdByDefault }));
  }, [createdByDefault]);

  // Assign users dialog
  const [assignedOpen, setAssignedOpen] = useState(false);
  const [assignedProject, setAssignedProject] = useState<ProjectRow | null>(null);
  const [assignedUsers, setAssignedUsers] = useState<AssignedUserRow[]>([]);
  const [assignedLoading, setAssignedLoading] = useState(false);

  const openAssignedUsers = async (project: ProjectRow) => {
    setAssignedProject(project);
    setAssignedOpen(true);
    setAssignedLoading(true);
    setAssignedUsers([]);
    try {
      const usersData = await apiService.getAdminProjectAssignedUsers(project.id);
      setAssignedUsers(Array.isArray(usersData) ? usersData : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load assigned users');
    } finally {
      setAssignedLoading(false);
    }
  };

  const [savingRole, setSavingRole] = useState<Record<number, boolean>>({});

  const updateUserRole = async (userId: number, role: string) => {
    if (!assignedProject) return;
    setSavingRole((prev) => ({ ...prev, [userId]: true }));
    try {
      await apiService.updateAdminProjectAssignedUserRole(assignedProject.id, userId, role);
      // Refresh assigned list
      const usersData = await apiService.getAdminProjectAssignedUsers(assignedProject.id);
      setAssignedUsers(Array.isArray(usersData) ? usersData : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update role');
    } finally {
      setSavingRole((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleCreateProject = async () => {
    if (!createForm.name.trim()) {
      setError('Project name is required');
      return;
    }
    const tenantIdNum = Number(createForm.tenantId);
    if (!tenantIdNum) {
      setError('tenantId (organization id) is required');
      return;
    }
    if (!createForm.createdBy.trim()) {
      setError('createdBy is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await apiService.createAdminProject({
        tenantId: tenantIdNum,
        name: createForm.name.trim(),
        description: createForm.description,
        createdBy: createForm.createdBy.trim(),
      });
      setCreateOpen(false);
      setCreateForm({
        tenantId: '',
        name: '',
        description: '',
        createdBy: createdByDefault,
      });
      await loadProjects();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const archiveProject = async (projectId: number) => {
    setLoading(true);
    setError(null);
    try {
      await apiService.archiveAdminProject(projectId);
      await loadProjects();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to archive project');
    } finally {
      setLoading(false);
    }
  };

  const restoreProject = async (projectId: number) => {
    setLoading(true);
    setError(null);
    try {
      await apiService.restoreAdminProject(projectId);
      await loadProjects();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to restore project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 800 }}>
        🏢 Project & Resource Management
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Create Project
        </Button>
        <Button variant={includeArchived ? 'contained' : 'outlined'} onClick={() => setIncludeArchived((v) => !v)}>
          {includeArchived ? 'Showing Archived' : 'Include Archived'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={22} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Test Cycles</TableCell>
              <TableCell>Activity (PROJECT)</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.name}</Typography>
                    {p.description ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 380, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.description}
                      </Typography>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell>{p.tenantId ?? '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={p.deletedAt ? 'default' : 'success'}
                    label={p.deletedAt ? 'ARCHIVED' : 'ACTIVE'}
                  />
                </TableCell>
                <TableCell>{p.testCyclesCount ?? 0}</TableCell>
                <TableCell>{p.activityCount ?? 0}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openAssignedUsers(p)} sx={{ mr: 1 }}>
                    Assign Users
                  </Button>
                  {p.deletedAt ? (
                    <Button size="small" variant="outlined" onClick={() => restoreProject(p.id)}>
                      Restore
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" color="error" onClick={() => archiveProject(p.id)}>
                      Archive
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {projects.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No projects found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create Project Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <TextField
              label="Tenant (organization id / tenantId)"
              size="small"
              value={createForm.tenantId}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, tenantId: e.target.value }))}
            />
            <TextField
              label="Project Name"
              size="small"
              value={createForm.name}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              label="Description"
              size="small"
              multiline
              rows={3}
              value={createForm.description}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              label="Created By"
              size="small"
              value={createForm.createdBy}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, createdBy: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} variant="text">
            Cancel
          </Button>
          <Button onClick={handleCreateProject} variant="contained" disabled={loading || !createForm.name.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Users Dialog */}
      <Dialog open={assignedOpen} onClose={() => setAssignedOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Assign Users
          {assignedProject ? ` — ${assignedProject.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {assignedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Save</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedUsers.map((u) => (
                    <TableRow key={u.userId}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {u.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Select
                          size="small"
                          value={u.role || 'MEMBER'}
                          onChange={(e) =>
                            setAssignedUsers((prev) =>
                              prev.map((x) => (x.userId === u.userId ? { ...x, role: e.target.value as string } : x))
                            )
                          }
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <MenuItem key={r} value={r}>
                              {r}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>{u.status || '-'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!!savingRole[u.userId]}
                          onClick={() => updateUserRole(u.userId, u.role || 'MEMBER')}
                        >
                          {savingRole[u.userId] ? 'Saving...' : 'Update'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {assignedUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No users found for this tenant.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignedOpen(false)} variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminProjectManagementPage;

